import { dateFromWeekNumber } from '.';
import { ICampaign, IDevice } from '../interfaces';
const MAX_SLOTS_DEVICE = 5;
const MAX_DEV_HOURS = 15;

export interface IWeekData {
    campaigns: (ICampaign | null)[];
    dateStart: Date;
}

export const getOneJan = (date: Date) => {
    const now = date;

    return dateFromWeekNumber(1, now);
};

export const generateDevicesHashMap = (devices: IDevice[]) => {
    const data: { [key: number]: IDevice } = {};

    for (const device of devices) {
        data[device.id] = device;
    }

    return data;
};

export const generateWeeksData = (campaigns: ICampaign[], now = new Date()) => {
    const weeks: IWeekData[] = [];

    let maxCampaigns = 0;
    campaigns.sort((a, b) => {
        const aTime = a.endDate.getTime();
        const endDate = new Date(b.endDate);

        if (endDate.getDay() !== 0) {
            endDate.setDate(endDate.getDate() + (7 - endDate.getDay()));
        }

        const bTime = endDate.getTime();

        if (aTime === bTime) {
            return a.startDate.getTime() - b.startDate.getTime();
        }

        return bTime - aTime;
    });

    {
        const oneJan = getOneJan(now);
        for (let i = 0; i < 52; i++) {
            weeks.push({ campaigns: [], dateStart: new Date(oneJan) });
            oneJan.setDate(oneJan.getDate() + 7);
        }
    }

    for (const c of campaigns) {
        const oneJan = getOneJan(now);

        for (let i = 0; i < 52; i++) {
            const end = new Date(oneJan);
            end.setDate(end.getDate() + 7);

            if (c.startDate >= end || c.endDate < oneJan) {
                oneJan.setDate(oneJan.getDate() + 7);
                continue;
            }

            if (c.endDate < oneJan) {
                break;
            }

            weeks[i].campaigns.push(c);

            maxCampaigns = Math.max(maxCampaigns, weeks[i].campaigns.length);

            oneJan.setDate(oneJan.getDate() + 7);
        }
    }

    for (let i = 1; i < 52; i++) {
        for (let j = 0; j < weeks[i].campaigns.length; j++) {
            for (let k = j + 1; k < weeks[i - 1].campaigns.length; k++) {
                const weekA = weeks[i - 1].campaigns[k];
                const weekB = weeks[i].campaigns[j];

                if (!weekA || !weekB) {
                    continue;
                }

                if (weekA.id === weekB.id) {
                    for (let l = 0; l < k - j; l++) {
                        weeks[i].campaigns.splice(j, 0, null);
                    }
                }
            }
        }

        while (weeks[i].campaigns.length < maxCampaigns) {
            weeks[i].campaigns.push(null);
        }
    }

    while (weeks[0].campaigns.length < maxCampaigns) {
        weeks[0].campaigns.push(null);
    }

    return { weeks, maxCampaigns };
};

export const generateDeviceOccupation = (
    campaigns: ICampaign[],
    devices: number[],
    planning: null | number[][] = null,
    startDate: Date
) => {
    const data: { [key: number]: number } = {};

    const isPlanningMatching = (campaign: ICampaign) => {
        if (planning === null) return true;

        const d = new Date(startDate);
        for (let i = 0; i < 7; i++) {
            if (campaign.startDate > d || campaign.endDate < d) {
                d.setDate(d.getDate() + 1);
                continue;
            }

            for (const h of campaign.planning[i]) {
                if (planning[i].indexOf(h) !== -1) {
                    return true;
                }
            }

            d.setDate(d.getDate() + 1);
        }

        return false;
    };

    for (const c of campaigns) {
        if (planning !== null && !isPlanningMatching(c)) {
            continue;
        }

        for (const d of c.devices) {
            if (!data[d]) {
                data[d] = c.slots;
            } else {
                data[d] += c.slots;
            }
        }
    }

    for (const d of devices) {
        if (!data[d]) {
            data[d] = 0;
        }
    }

    return data;
};

export const getSlotsAvailable = (campaigns: ICampaign[], devices: number[], startDate: Date) => {
    const date = new Date(startDate);
    let maxSlots = 0;

    for (let i = 0; i < 7; i++) {
        for (let h = 7; h <= 22; h++) {
            const hData: { [key: number]: number } = {};
            for (const d of devices) {
                hData[d] = 0;
            }

            for (const campaign of campaigns) {
                if (campaign.startDate > date || campaign.endDate < date || !campaign.planning[i].includes(h)) continue;

                for (const d of campaign.devices) {
                    if (d in hData) {
                        hData[d] += campaign.slots;
                    }
                }
            }

            maxSlots = Math.max(...Object.values(hData), maxSlots);
        }

        date.setDate(date.getDate() + 1);
    }

    return Math.max(MAX_SLOTS_DEVICE - maxSlots, 0);
};

export const getHoursSumFromPlanning = (planning: number[][]) => {
    let hoursSum = 0;

    for (const day of planning) {
        hoursSum += day.length >= 15 ? 15 : day.length;
    }

    return hoursSum;
};

export const getUsedSpace = (
    campaigns: ICampaign[],
    devices_nb: number,
    dateStart: Date,
    artificial_device_number: null | number = null
) => {
    const total_hours = devices_nb * MAX_DEV_HOURS * 7 * MAX_SLOTS_DEVICE;
    let campaigns_hours = 0;

    const dateEnd = new Date(dateStart);
    dateEnd.setDate(dateEnd.getDate() + 7);

    for (const c of campaigns) {
        const assignedDevcies = artificial_device_number === null ? c.devices.length : artificial_device_number;

        if (c.endDate >= dateEnd) {
            for (const day of c.planning) {
                campaigns_hours += assignedDevcies * (day.length >= 15 ? 15 : day.length);
            }
        } else {
            const start = new Date(dateStart);
            while (start < c.endDate) {
                let day = start.getDay() - 1;
                if (day === -1) {
                    day = 6;
                }

                campaigns_hours += assignedDevcies * (c.planning[day].length >= 15 ? 15 : c.planning[day].length);

                start.setDate(start.getDate() + 1);
            }
        }
    }

    return campaigns_hours / total_hours;
};

export const getSignsFromDevices = (devices: IDevice[]) => {
    const signs: { [key: string]: number } = {};

    for (const d of devices) {
        const signName: string = (d as any).Shop.Sign.name;
        if (signName in signs) {
            signs[signName]++;
        } else {
            signs[signName] = 1;
        }
    }

    return signs;
};

export const alignWeeks = (weeks: IWeekData[]) => {
    const lastWeekOrder: { [key: string]: number } = {};

    for (const w of weeks) {
        const new_week = new Array(w.campaigns.length).fill(null);
        const week_campaigns: {[key: string]: ICampaign} = {};
        for (const c of w.campaigns) {
            if (c !== null) week_campaigns[c.name] = c;
        }

        // Insert ordered campaigns
        for (const id in lastWeekOrder) {
            if (id in week_campaigns) {
                new_week[lastWeekOrder[id]] = week_campaigns[id];
            } else {
                // Remove from list, as we dont have this campaign anymore
                delete lastWeekOrder[id];
            }
        }

        // Insert new campaigns
        for (const c of w.campaigns) {
            if (c === null)
                break;

            if (c.name in lastWeekOrder)
                continue; // Already inserted

            // Find a free position to insert this campaign
            for (let i = 0; i < new_week.length; i++) {
                if (new_week[i] === null) {
                    // We found a free spot
                    new_week[i] = c;
                    lastWeekOrder[c.name] = i;
                    break;
                }
            }
        }

        w.campaigns = new_week;
    }
};
